// context/AuthContext.tsx
import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import APIService from '../utils/api';
import MixpanelService from '../utils/mixpanel';

interface User {
  _id: string;
  email?: string;
  mobile?: string;
  firstName?: string;
  lastName?: string;
  address?: {
    country?: string;
    street?: string;
    town?: string;
    province?: string;
    postCode?: string;
  }
  // Add more fields as needed
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  refreshUser: () => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  refreshUser: () => {},
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    const token = Cookies.get('token');
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      const data = await APIService.me();
      MixpanelService.identify(data.email);
      setUser(data);
    } catch (err) {
      Cookies.remove('token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAuthenticated: !!user,
        refreshUser: fetchUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// 🔁 Custom hook to use anywhere
export const useAuth = () => useContext(AuthContext);
